/**
 * Promotion API สำหรับ user-modern
 * เรียก promotion service ผ่าน gateway: /api/v1/promotion -> promotion:4305/api/promotions
 * docker-compose: promotion service อยู่ที่ port 4305, PROMOTION_URL=http://huay-v4-production-lb:8080/api/v1/promotion
 */
import { handleSessionReplaced } from '../utils/sessionReplacedHandler'
import { getRuntimeTenantIdSync } from '../lib/runtimeTenant'

const getBaseURL = () =>
  (import.meta.env.VITE_API_BASE_URL as string) || 'http://localhost:8081'

function getAccessToken(): string | null {
  return localStorage.getItem('accessToken')
}

function getTenantId(): string {
  try {
    const s = localStorage.getItem('user')
    const user = s ? JSON.parse(s) : null
    if (user?.tenantId) return user.tenantId
  } catch {}
  const resolved = getRuntimeTenantIdSync()
  if (resolved) return resolved
  throw new Error('Tenant ID is required but not found.')
}

async function promotionFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getAccessToken()
  const tenantId = getTenantId()
  const url = `${getBaseURL()}${path}`
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-Tenant-ID': tenantId,
      ...(options?.headers as Record<string, string>),
    },
    credentials: 'include',
  })
  const data = (await res.json()) as T & { code?: string; message?: string }
  if (res.status === 401 && (data as any)?.code === 'SESSION_REPLACED') {
    await handleSessionReplaced()
    throw new Error('SESSION_REPLACED')
  }
  if (!res.ok) {
    const err = new Error((data as any)?.message || `HTTP ${res.status}`) as Error & { status?: number }
    ;(err as any).status = res.status
    throw err
  }
  return data
}

/** โครงจาก promotion service: campaign ใน dashboard (getDashboard) */
export interface PromotionCampaignItem {
  campaignId?: string
  _id?: string
  name: string
  description?: string
  type?: string
  status?: string
  priority?: number
  display?: {
    title?: string
    subtitle?: string
    description?: string
    imageUrl?: string
    bannerUrl?: string
    iconUrl?: string
    buttonText?: string
    displayOrder?: number
  }
  schedule?: {
    startDate?: string
    endDate?: string
  }
  settings?: {
    bonusType?: string
    bonusAmount?: number
  }
}

export interface PromotionDashboardResponse {
  success: boolean
  data?: {
    campaigns?: {
      active?: PromotionCampaignItem[]
    }
  }
  service?: string
}

export interface PromotionCampaignListResponse {
  success: boolean
  data?: {
    campaigns?: PromotionCampaignItem[]
    pagination?: {
      page: number
      limit: number
      total: number
      pages: number
    }
  }
  service?: string
}

export interface PromotionApplyResponse {
  success: boolean
  data?: unknown
  message?: string
}

const sortActiveCampaigns = (campaigns: PromotionCampaignItem[]): PromotionCampaignItem[] =>
  [...campaigns].sort((left, right) => {
    const leftOrder = Number(left.display?.displayOrder ?? Number.MAX_SAFE_INTEGER)
    const rightOrder = Number(right.display?.displayOrder ?? Number.MAX_SAFE_INTEGER)
    const leftPriority = Number(left.priority ?? 0)
    const rightPriority = Number(right.priority ?? 0)

    return leftOrder - rightOrder || rightPriority - leftPriority || left.name.localeCompare(right.name)
  })

/**
 * ดึงรายการ campaign ที่ active
 * ใช้ route เดียวกับฝั่ง admin ก่อน เพราะ gateway รองรับแน่นอน
 * แล้วค่อย fallback ไป dashboard route เก่าเพื่อความเข้ากันได้ย้อนหลัง
 */
export async function getPromotionDashboard(): Promise<PromotionCampaignItem[]> {
  try {
    const res = await promotionFetch<PromotionCampaignListResponse>(
      '/api/v1/promotion/campaigns?status=active&limit=20&sortBy=priority&sortOrder=desc'
    )
    if (!res.success || !Array.isArray(res.data?.campaigns)) return []
    return sortActiveCampaigns(
      res.data.campaigns.filter((campaign) => !campaign.status || campaign.status === 'active')
    )
  } catch (error) {
    const status = (error as { status?: number } | undefined)?.status
    if (status && status !== 404) {
      throw error
    }
  }

  const fallback = await promotionFetch<PromotionDashboardResponse>('/api/v1/promotion')
  if (!fallback.success || !fallback.data?.campaigns?.active) return []
  return sortActiveCampaigns(fallback.data.campaigns.active)
}

/**
 * กดรับโบนัส (apply campaign ให้ user)
 */
export async function applyPromotion(promotionId: string, promotionType: 'campaign' | 'bonus'): Promise<PromotionApplyResponse> {
  const userId = (() => {
    try {
      const s = localStorage.getItem('user')
      const user = s ? JSON.parse(s) : null
      return (user?.id || user?._id) ?? ''
    } catch {
      return ''
    }
  })()
  const res = await promotionFetch<PromotionApplyResponse>('/api/v1/promotion/apply', {
    method: 'POST',
    body: JSON.stringify({ userId, promotionId, promotionType }),
  })
  return res
}
